import { useState, useEffect } from 'react'
import FormModal from '../FormModal'

const formStyles = {
  field: {
    marginBottom: '0.4rem',
  },
  label: {
    display: 'block',
    color: 'var(--text-primary)',
    marginBottom: '0.4rem',
    fontSize: '0.95rem',
    fontWeight: 500,
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    background: 'var(--accent-deep)',
    border: '1px solid var(--accent-teal)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
  },
  textarea: {
    width: '100%',
    padding: '0.75rem',
    background: 'var(--accent-deep)',
    border: '1px solid var(--accent-teal)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    minHeight: '120px',
    resize: 'vertical',
  },
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  checkboxInput: {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
  },
  hint: {
    fontSize: '0.85rem',
    color: 'var(--text-muted)',
    marginTop: '0.25rem',
  },
}

// Stored in DB as a JSON array string (e.g. '["Event 1","Event 2"]') for historical reasons,
// but the form accepts/shows a plain comma-separated string for easier editing.
const jsonArrayToCommaString = (raw) => {
  if (!raw) return ''
  try {
    const arr = JSON.parse(raw)
    if (Array.isArray(arr)) return arr.join(', ')
  } catch {
    // Not valid JSON — fall through and return the raw value so existing bad data is still editable
  }
  return raw
}

const commaStringToJsonArray = (input) => {
  const trimmed = (input || '').trim()
  if (!trimmed) return ''
  const arr = trimmed.split(',').map(t => t.trim()).filter(Boolean)
  return JSON.stringify(arr)
}

function SessionForm({ isOpen, onClose, session = null, onSubmit }) {
  const [formData, setFormData] = useState({
    title: '',
    session_date: '',
    summary: '',
    key_events: '',
    is_latest: 0,
    is_visible: 0,
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Update form data when session prop changes
  useEffect(() => {
    if (session) {
      setFormData({
        title: session.title || '',
        session_date: session.session_date || '',
        summary: session.summary || '',
        key_events: jsonArrayToCommaString(session.key_events),
        is_latest: session.is_latest || 0,
        is_visible: session.is_visible !== undefined ? session.is_visible : 0,
      })
    } else {
      setFormData({
        title: '',
        session_date: '',
        summary: '',
        key_events: '',
        is_latest: 0,
        is_visible: 0,
      })
    }
  }, [session])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Convert the user-friendly comma string back to the JSON array string the API expects
      const normalized = {
        ...formData,
        key_events: commaStringToJsonArray(formData.key_events),
      }
      await onSubmit(normalized)
      onClose()
    } catch (error) {
      console.error('Form submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={session ? 'Edit Session' : 'Create New Session'}
      isSubmitting={isSubmitting}
    >
      <div style={formStyles.field}>
        <label style={formStyles.label} htmlFor="title">Title *</label>
        <input
          type="text"
          id="title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          required
          style={formStyles.input}
          placeholder="Session 1: The Adventure Begins"
        />
      </div>

      <div style={formStyles.field}>
        <label style={formStyles.label} htmlFor="session_date">Session Date *</label>
        <input
          type="date"
          id="session_date"
          name="session_date"
          value={formData.session_date}
          onChange={handleChange}
          required
          style={formStyles.input}
        />
      </div>

      <div style={formStyles.field}>
        <label style={formStyles.label} htmlFor="summary">Summary *</label>
        <textarea
          id="summary"
          name="summary"
          value={formData.summary}
          onChange={handleChange}
          required
          style={{ ...formStyles.textarea, minHeight: 'clamp(260px, 52vh, 680px)' }}
          placeholder="Enter session summary"
        />
      </div>

      <div style={formStyles.field}>
        <label style={formStyles.label} htmlFor="key_events">Key Events</label>
        <input
          type="text"
          id="key_events"
          name="key_events"
          value={formData.key_events}
          onChange={handleChange}
          style={formStyles.input}
          placeholder="Party met Lord Neverember, Discovered the secret passage"
        />
        <div style={formStyles.hint}>
          Separate events with commas. (Commas inside a single event will split it — phrase around them.)
        </div>
      </div>

      <div style={{ ...formStyles.field, display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
        <label style={formStyles.checkbox}>
          <input
            type="checkbox"
            name="is_latest"
            checked={formData.is_latest === 1}
            onChange={handleChange}
            style={formStyles.checkboxInput}
          />
          <span style={{ color: 'var(--text-primary)', fontSize: '0.95rem' }}>
            Mark as latest session
          </span>
        </label>

        <label style={formStyles.checkbox}>
          <input
            type="checkbox"
            name="is_visible"
            checked={formData.is_visible === 1}
            onChange={handleChange}
            style={formStyles.checkboxInput}
          />
          <span style={{ color: 'var(--text-primary)', fontSize: '0.95rem' }}>
            Visible to players
          </span>
        </label>
      </div>
    </FormModal>
  )
}

export default SessionForm
